import { ToastContainer } from "react-toastify/unstyled";

import { isPaladinApiError } from "@paladin/errors";
import { i18n } from "@paladin/i18n";

import { toast } from "~/pld/ui/toast";

import "react-toastify/dist/ReactToastify.css";

import type { ContractErrors } from "@paladin/contract";
import type { EmployeeExpectedInfo } from "@paladin/domain";
import type { PaladinErrorCode } from "@paladin/errors";
import type { i18nKey } from "@paladin/i18n";

import { isDefinedError, ORPCError } from "@orpc/client";

import { serverErrorToI18n } from "~/pld/lib/server-error-to-i18n";

/**
 * `isDefinedError` narrows `ContractErrors` down to `never`, so a defined error is
 * read through this shape until every route is migrated to oRPC.
 */
type DefinedContractError =
  | {
      code: "MISSING_EXPECTED_INFO_FOR_BOOKING";
      data: { missingExpectedInfos: Array<EmployeeExpectedInfo> };
    }
  | { code: Exclude<PaladinErrorCode, "MISSING_EXPECTED_INFO_FOR_BOOKING"> };

/** Inline labels for the toast; the collaborator callouts keep their own richer copy. */
const MISSING_EXPECTED_INFO_I18N_KEYS = {
  BIRTH_DATE: "errors:missingExpectedInfo.birthDate",
  CONTRACT_TYPE: "errors:missingExpectedInfo.contractType",
  JOB_TITLE: "errors:missingExpectedInfo.jobTitle",
  MEDICAL_DATA: "errors:missingExpectedInfo.checkupDates",
  PROFESSIONAL_RISK: "errors:missingExpectedInfo.risk",
} as const satisfies Record<EmployeeExpectedInfo, i18nKey>;

const missingExpectedInfoMessage = (
  missingExpectedInfos: ReadonlyArray<EmployeeExpectedInfo>,
) =>
  i18n.t("errors:MISSING_EXPECTED_INFO_FOR_BOOKING", {
    missingInfos: missingExpectedInfos
      .map((missingInfo) =>
        i18n.t(MISSING_EXPECTED_INFO_I18N_KEYS[missingInfo]),
      )
      .join(", "),
  });

export const PaladinToastContainer = () => {
  return <ToastContainer position="bottom-right" />;
};

/**
 * @deprecated use toastORPCError instead to display ORPC errors
 */
export const toastPaladinApiError = (error: unknown, defaultError?: string) => {
  // Fallback to avoid showing default errors for ORPC errors
  if (error instanceof ORPCError) {
    toastORPCError(error);
    return null;
  }
  if (error) {
    if (isPaladinApiError(error)) {
      toast.error(i18n.t(serverErrorToI18n(error.errorCode)));
    } else {
      toast.error(defaultError ?? i18n.t("errors:DEFAULT_SERVER_ERROR"));
    }
  }
  return null;
};

export const toastORPCError = (
  error: ContractErrors,
  defaultError?: string,
) => {
  if (!isDefinedError(error)) {
    toast.error(defaultError ?? i18n.t("errors:DEFAULT_SERVER_ERROR"));
    return null;
  }

  const definedError = error as unknown as DefinedContractError;

  if (definedError.code === "MISSING_EXPECTED_INFO_FOR_BOOKING") {
    toast.error(
      missingExpectedInfoMessage(definedError.data.missingExpectedInfos),
    );
    return null;
  }

  toast.error(i18n.t(serverErrorToI18n(definedError.code)));

  return null;
};
